<template>
  <div>
    <div class="wz-page-header flex items-center justify-between">
      <h1 class="wz-page-title mb-0">{{ editing ? 'Editar plantilla' : 'Nueva plantilla' }}</h1>
      <wz-button variant="light" @click="goBack">Volver</wz-button>
    </div>

    <form @submit.prevent="save" novalidate>
      <div class="flex flex-wrap -mx-2">
        <!-- Panel principal: formulario -->
        <div class="w-full lg:w-2/3 px-2">
          <wz-card :title="editing ? 'Datos de la plantilla' : 'Datos de la nueva plantilla'">
            <div class="flex flex-wrap -mx-2">
              <div class="w-full md:w-1/2 mb-3 px-2">
                <wz-input
                  label="Nombre"
                  required
                  v-model="form.name"
                  placeholder="Nombre de la plantilla"
                  :error="$v.form.name.$error && !$v.form.name.required ? 'El nombre es requerido' : ''"
                />
              </div>
              <div class="w-full md:w-1/2 mb-3 px-2">
                <wz-select
                  label="Empresa"
                  required
                  v-model="form.companyId"
                  :items="companies"
                  defaultOption="Seleccionar empresa"
                  :disabled="editing"
                  :error="$v.form.companyId.$error && !$v.form.companyId.required ? 'La empresa es requerida' : ''"
                />
              </div>

              <div class="w-full mb-3 px-2">
                <label class="wz-form-label">Descripción</label>
                <textarea v-model="form.description" class="wz-form-control w-full" rows="2" placeholder="Opcional" />
              </div>

              <div class="w-full mb-3 px-2">
                <label class="wz-form-label">
                  Tipos de contrato aplicables <span class="text-danger">*</span>
                </label>
                <div class="border rounded p-2" style="max-height: 220px; overflow-y: auto;">
                  <label v-for="ct in contractTypeOptions" :key="ct.key" class="flex items-center mb-1">
                    <input type="checkbox" :value="ct.key" v-model="form.contractTypes" class="mr-2" />
                    <span>{{ ct.label }}</span>
                  </label>
                </div>
                <small v-if="$v.form.contractTypes.$error" class="text-danger">Seleccione al menos un tipo</small>

                <wz-alert v-if="duplicates.length" variant="warning" class="mt-2 mb-0">
                  Ya existe(n) {{ duplicates.length }} plantilla(s) activa(s) para este tipo:
                  <ul class="mt-1 mb-0" style="padding-left: 1.2rem;">
                    <li v-for="d in duplicates" :key="d.id">{{ d.name }}</li>
                  </ul>
                  Puede mantener todas activas o desactivar las anteriores desde el listado.
                </wz-alert>
              </div>

              <div class="w-full mb-3 px-2">
                <label class="wz-form-label">
                  Archivo DOCX <small class="text-muted">(.docx con placeholders {{ '${grupo.campo}' }})</small>
                  <span v-if="editing" class="text-muted"> — opcional (solo si quieres reemplazarlo)</span>
                </label>
                <wz-file-input :key="fileKey" id-file="contract-template-file" :accept-file="['.docx']" @onChange="onFileChange" />
              </div>
            </div>

            <div class="flex justify-end gap-2 mt-2">
              <wz-button variant="light" @click="goBack">Cancelar</wz-button>
              <wz-button type="submit" variant="primary" class="ml-2" :disabled="submitting">
                {{ submitting ? 'Guardando...' : 'Guardar' }}
              </wz-button>
            </div>
          </wz-card>
        </div>

        <!-- Panel lateral: variables disponibles -->
        <div class="w-full lg:w-1/3 px-2">
          <wz-card title="Variables disponibles">
            <wz-input v-model="varSearch" placeholder="Buscar variable..." class="mb-3" />
            <div style="max-height: 460px; overflow-y: auto;">
              <div v-for="ph in filteredPlaceholders" :key="ph.variable" class="mb-2 pb-2 border-b">
                <div class="text-xs text-muted">{{ ph.group }} — {{ ph.label }}</div>
                <div class="flex items-center justify-between">
                  <code class="text-xs">{{ ph.variable }}</code>
                  <wz-button variant="outline" size="sm" @click="copyVar(ph.variable)">Copiar</wz-button>
                </div>
                <div class="text-xs text-muted">Ej.: {{ ph.example !== '' ? ph.example : '(vacío)' }}</div>
              </div>
            </div>
          </wz-card>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import { required, minLength } from 'vuelidate/lib/validators'
import { CompanyService } from '@/core/services'
import ContractTemplateService from '@/core/services/ContractTemplateService'
import { Constants } from '@/core/utils'

export default {
  name: 'ContractTemplateEditor',
  data() {
    return {
      editing: false,
      submitting: false,
      companies: [],
      placeholders: [],
      contractTypeOptions: Object.values(Constants.ContractType).map(t => ({ key: t.key, label: t.label })),
      file: null,
      fileKey: 0,
      varSearch: '',
      duplicates: [],
      form: { name: null, description: null, companyId: null, contractTypes: [] },
    }
  },
  validations: {
    form: {
      name: { required },
      companyId: { required },
      contractTypes: { required, minLength: minLength(1) },
    },
  },
  computed: {
    filteredPlaceholders() {
      const q = this.varSearch.trim().toLowerCase()
      if (!q) return this.placeholders
      return this.placeholders.filter(p =>
        p.label.toLowerCase().includes(q) || p.variable.toLowerCase().includes(q) || p.group.toLowerCase().includes(q))
    },
  },
  watch: {
    'form.contractTypes'() { this.checkDuplicates() },
    'form.companyId'() { this.checkDuplicates() },
  },
  async created() {
    await this.loadCompanies()
    await this.loadPlaceholders()
    const id = this.$route.params.id
    if (id) {
      await this.load(id)
    } else if (this.$route.query.companyId) {
      this.form.companyId = Number(this.$route.query.companyId)
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'contract-template-list' })
    },
    async loadCompanies() {
      try {
        const resp = await CompanyService.listAll()
        this.companies = (resp.payload || []).map(c => ({ key: c.id, label: c.name }))
      } catch (e) {
        Alert.error('Error al cargar empresas')
      }
    },
    async loadPlaceholders() {
      try {
        const resp = await ContractTemplateService.placeholders()
        this.placeholders = resp.payload || []
      } catch (e) {
        this.placeholders = []
      }
    },
    async load(id) {
      try {
        this.$store.dispatch('app/loading', true)
        const t = (await ContractTemplateService.get(id)).payload
        this.form = {
          name: t.name,
          description: t.description,
          companyId: t.companyId,
          contractTypes: [...(t.contractTypes || [])],
        }
        this.editing = true
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar la plantilla')
        this.goBack()
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    onFileChange(files) {
      this.file = files && files.length > 0 ? files[0] : null
    },
    // Aviso informativo (no bloqueante): plantillas activas de la empresa que comparten algún tipo.
    // En edición se excluye el propio id para no mostrarse a sí misma.
    async checkDuplicates() {
      const types = this.form.contractTypes
      if (!this.form.companyId || !types || types.length === 0) {
        this.duplicates = []
        return
      }
      try {
        const excludeId = this.$route.params.id || null
        const resp = await ContractTemplateService.duplicates(this.form.companyId, types, excludeId)
        this.duplicates = resp.payload || []
      } catch (e) {
        this.duplicates = []
      }
    },
    copyVar(v) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(v).then(() => Alert.success('Variable copiada')).catch(() => {})
      }
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$error) return
      if (!this.editing && !this.file) {
        Alert.error('Debe adjuntar un archivo DOCX')
        return
      }
      try {
        this.submitting = true
        this.$store.dispatch('app/loading', true)
        if (this.editing) {
          await ContractTemplateService.updateMetadata(this.$route.params.id, {
            name: this.form.name, description: this.form.description, contractTypes: this.form.contractTypes,
          })
          if (this.file) {
            const fd = new FormData()
            fd.append('file', this.file)
            await ContractTemplateService.replaceFile(this.$route.params.id, fd)
          }
          Alert.success('Plantilla actualizada')
        } else {
          const fd = new FormData()
          fd.append('name', this.form.name)
          if (this.form.description) fd.append('description', this.form.description)
          this.form.contractTypes.forEach(ct => fd.append('contractTypes', ct))
          fd.append('file', this.file)
          await ContractTemplateService.create(this.form.companyId, fd)
          Alert.success('Plantilla creada')
        }
        this.goBack()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al guardar la plantilla')
      } finally {
        this.submitting = false
        this.$store.dispatch('app/loading', false)
      }
    },
  },
}
</script>

<style lang="stylus" scoped></style>
